import React, { FC, lazy } from 'react';
import { ReactComponent as CheckMark } from '@blink/assets/src/icons/checkCircleBorder.svg';

import { useDAppConnection } from '../hooks/useDAppConnection';
import { useWalletConnectUri } from '../hooks/useWalletConnectUri';
import { useWalletConnectHandler } from '../hooks/useWalletConnectHandler';

import { Button, ButtonSize, ButtonVariant, Input } from '@/shared/ui';
import { useHideModal } from '@/features/Modals';
import useLocalFeatureFlag from '@/shared/hooks/useLocalFeatureFlag';
import { Features } from '@/shared/configs/features';

const BaseConnectDAppModal: FC = () => {
    const { marginAccountAddress, connectedDapp } = useDAppConnection();
    const { walletConnectUri, handleChange } = useWalletConnectUri();
    const { connectWalletByUriHandler } = useWalletConnectHandler(
        marginAccountAddress,
        walletConnectUri,
    );
    const { handleCloseModal } = useHideModal();

    if (connectedDapp) {
        return (
            <div
                className='flex items-center w-[438px] px-5 py-5 gap-4 self-stretch 
                    my-7 mb-3 rounded-[radius-overall] bg-lightPositive 
                    text-green800 font-medium text-[16px] shadow-[0px_6px_12px_-3px_rgba(0,0,0,0.05)] 
                    border border-green200 [&>svg]:h-[24px] [&>svg]:w-[24px] [&>svg_path]:fill-green800'
            >
                <CheckMark />
                <div onClick={handleCloseModal} className='cursor-pointer'>
                    Successfully connected via WalletConnect
                </div>
            </div>
        );
    }

    return (
        <div className='flex items-center justify-between my-7 mb-3 w-[432px]'>
            <Input
                placeholder='Input'
                className='h-[44px] px-4 text-md w-[312px]'
                value={walletConnectUri}
                onChange={handleChange}
            />
            <div className='min-w-[112px] flex items-center ml-3 [&>button]:h-[44px] [&>button]:w-full [&>button]:text-md'>
                <Button
                    variant={ButtonVariant.Secondary}
                    size={ButtonSize.Medium}
                    onClick={connectWalletByUriHandler}
                >
                    Connect
                </Button>
            </div>
        </div>
    );
};

const LazyConnectDAppModal = lazy(() =>
    import('./v2/ConnectDAppModal').then((module) => ({
        default: module.ConnectDAppModal,
    })),
);

export const ConnectDAppModal: FC = () => {
    const isNewWalletConnect = useLocalFeatureFlag(Features.NewWalletConnect, true);

    if (!isNewWalletConnect) {
        return <BaseConnectDAppModal />;
    }
    return (
        <React.Suspense fallback={null}>
            <LazyConnectDAppModal />
        </React.Suspense>
    );
};
